import React from 'react';
import { dateFormatters } from '@insurancemenu/imenu-components-lib/dist/helpers';
import EnrollmentTag from 'components/common/enrollment/EnrollmentTag';
import { EnrollmentPeriod, EnrollmentStage, EnrollmentType } from 'types/enrollmentService';
import { useRootStore } from 'store/rootStore';
import { toJS } from 'mobx';
import StageButton from 'components/common/enrollment/StageButton';
import { Button } from '@insurancemenu/imenu-components-lib';
import { sortByProductLineId } from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { useHistory } from 'react-router-dom';
import FormsSpinner from 'components/common/loaders/FormSpinner';
import { observer } from 'mobx-react-lite';
import ProductLineTitle from 'components/common/general/ProductLineTitle';
import clsx from 'clsx';
import { createStyles } from './styles';
import RenewalPortfolioCard from './RenewalPortfolioCard';
import { getMaxRateCount, hasMultiProductsPortfolio, hasRenewalRates } from '../utils';
import InstalledPortfolioCard from '../InstalledEnrollmentCard/InstalledPortfolioCard';

interface RenewalEnrollmentCardProps {
  enrollmentPeriod: EnrollmentPeriod;
  footer: JSX.Element | null;
  onContinue?: () => void;
}

const RenewalEnrollmentCard = observer(
  ({ enrollmentPeriod, footer, onContinue }: RenewalEnrollmentCardProps): JSX.Element => {
    const { id, installedProposal, effectiveDate, hasConfirmedProposal } = enrollmentPeriod;

    const classes = createStyles({ footer, hasConfirmedProposal });

    // Mobx store
    const {
      userStore: { userRoles },
      enrollmentCardStore: { getPeriodLoading },
    } = useRootStore();

    // Common data
    const history = useHistory();
    const convertedUserRoles = userRoles.map((role) => toJS(role)).slice();

    const redirect = (url: string): void => {
      history.push(url);
    };

    const onContinueClick = (): void => {
      if (onContinue) {
        onContinue();
      } else {
        redirect(`/checklist/${enrollmentPeriod.id}`);
      }
    };

    const cardsList = installedProposal?.productLines
      .sort((a, b) => sortByProductLineId(a.id, b.id))
      .filter((productLineData) => productLineData.products.length > 0)
      .map((productLineData) => {
        return (
          <div className={classes.productLineSection} key={`${id}-${productLineData.id}`}>
            <ProductLineTitle productLineId={productLineData.id} styles={classes.productLineTitle} />
            <div
              className={
                productLineData.products.length === 1
                  ? clsx(
                      classes.cardsWrapperOneProduct,
                      // keep the installed card at half width when there is no renewal card next to it
                      !hasRenewalRates(productLineData) && classes.cardsWrapperWithoutRenewal,
                    )
                  : classes.cardsWrapperMultiProducts
              }
            >
              <InstalledPortfolioCard
                productLineData={productLineData}
                maxRatesCount={getMaxRateCount(installedProposal?.productLines || [], EnrollmentType.RENEWAL)}
                hasMultiProductsPortfolio={installedProposal ? hasMultiProductsPortfolio(installedProposal) : false}
                effectiveDate={effectiveDate}
                stage={EnrollmentStage.RENEWAL}
              />
              <RenewalPortfolioCard
                productLineData={productLineData}
                enrollmentPeriod={{ installedProposal, effectiveDate }}
              />
            </div>
          </div>
        );
      });

    const continueButton = (): JSX.Element | null => {
      if (!enrollmentPeriod.hasConfirmedProposal) {
        return null;
      }

      return (
        <Button
          color="primary"
          size="small"
          width="18.4rem"
          className={classes.continueButton}
          onClick={onContinueClick}
        >
          Continue
        </Button>
      );
    };

    return (
      <div className={classes.enrollmentCard}>
        <FormsSpinner display={getPeriodLoading(id)} />
        <header className={classes.cardHeader}>
          <div className={classes.leftHeaderSection}>
            <div>
              <h6 className={classes.effectiveDateTitle}>Renewal Date</h6>
              <h5 className={classes.effectiveDateValue}>
                {dateFormatters.normDateToShortAmericanDate(effectiveDate)}
              </h5>
            </div>
            <div className={classes.stageTagContainer}>
              <EnrollmentTag userRoles={convertedUserRoles} enrollmentPeriod={enrollmentPeriod} />
            </div>
          </div>
          {continueButton()}
        </header>
        <footer className={classes.footer}>
          {enrollmentPeriod.hasConfirmedProposal && footer && (
            <div className={classes.proposalInfo}>
              {footer}
              <StageButton enrollmentPeriod={enrollmentPeriod} />
            </div>
          )}
          {cardsList && cardsList.length > 0 && !enrollmentPeriod.hasConfirmedProposal && (
            <div className={classes.portfoliosList}>{cardsList}</div>
          )}
        </footer>
      </div>
    );
  },
);

export default RenewalEnrollmentCard;
